import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';
import awsExports from '../aws-exports';

// Amplify regeneration renamed the REST API entry from 'sleeptrackRest' to the
// raw API Gateway id (f4e0999 template migration), so never trust the name
// alone — the app has exactly one REST API, so fall back to the sole entry.
const API_NAME = 'sleeptrackRest';
const apis = awsExports.aws_cloud_logic_custom || [];
const apiConfig = apis.find(c => c.name === API_NAME) || apis[0];
const BASE_URL = apiConfig?.endpoint || '';

export const API_BASE_URL = BASE_URL;

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function getIdToken() {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    return session.tokens?.idToken?.toString() || '';
  } catch {
    return '';
  }
}

const REQUEST_TIMEOUT_MS = 25000;

async function apiRequest(method, path, body = null) {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeaders()),
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const options = { method, headers, signal: controller.signal };
  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    options.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, options);
  } catch (networkErr) {
    const message = networkErr?.name === 'AbortError'
      ? 'Request timed out — please try again'
      : 'Network error — please check your connection';
    toast.error(message);
    throw networkErr;
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 401) {
    toast.error('Session expired — please sign in again');
    if (typeof window !== 'undefined') window.location.href = '/login';
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = data.error || data.message || `HTTP ${response.status}`;
    toast.error(message);
    const err = new Error(message);
    err.status = response.status;
    err.response = { status: response.status, data };
    throw err;
  }

  return response.json();
}

// Background ops (IAP receipt re-delivery, subscription sync) — no toast on failure.
// Returns null on any error so callers can branch without try/catch.
export async function silentFetch(path, method = 'GET', body = null) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const headers = { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
    const options = { method, headers, signal: controller.signal };
    if (body && method !== 'GET' && method !== 'DELETE') options.body = JSON.stringify(body);
    const res = await fetch(`${BASE_URL}${path}`, options);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function apiGet(path, params) {
  const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
  return apiRequest('GET', `${path}${qs}`);
}
export async function apiPost(path, body) { return apiRequest('POST', path, body); }
export async function apiPut(path, body) { return apiRequest('PUT', path, body); }
export async function apiDelete(path) { return apiRequest('DELETE', path); }

// Sleep endpoints
export async function postSleep(sleepEntry) {
  return apiRequest('POST', '/api/sleep', sleepEntry);
}
export async function getSleepHistory(from, to) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return apiRequest('GET', `/api/sleep${qs ? `?${qs}` : ''}`);
}
export async function getSleepToday() { return apiRequest('GET', '/api/sleep/today'); }
export async function deleteSleep(date) {
  return apiRequest('DELETE', `/api/sleep/${encodeURIComponent(date)}`);
}
export async function getStats(period = 'week') { return apiRequest('GET', `/api/stats?period=${period}`); }
export async function postFactors(factors) { return apiRequest('POST', '/api/factors', factors); }
export async function getScoreBreakdown(date) { return apiRequest('GET', `/api/score/${date}`); }
export async function getInsights() { return apiRequest('GET', '/api/insights'); }
export async function getProfile() { return apiRequest('GET', '/api/profile'); }
export async function updateProfile(updates) { return apiRequest('PUT', '/api/profile', updates); }

// Account
export async function deleteAccount() { return apiRequest('DELETE', '/api/user/account'); }

// Subscription
export async function getSubscription() { return apiRequest('GET', '/api/subscription'); }
export async function verifyAppleReceipt(transactionId, productId) {
  return apiRequest('POST', '/api/subscription/apple/verify-receipt', {
    transactionId,
    productId,
  });
}

// Health check (unauthenticated)
export async function healthCheck() {
  const response = await fetch(`${BASE_URL}/api/health`);
  return response.json();
}
